import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Spinner } from "./spinner";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-[10px] whitespace-nowrap font-inter font-medium focus-visible:outline-none focus-visible:ring-0 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 relative",
  {
    variants: {
      variant: {
        // Primary contained button
        contained: "bg-primary-500 text-white hover:bg-primary-600 dark:bg-primary-400 dark:hover:bg-primary-500",
        contained1: "bg-primary-600 text-white hover:bg-primary-700 dark:bg-primary-600 dark:hover:bg-primary-700",
        button: "text-primary-500 rounded-none border border-transparent hover:border-primary-500 bg-transparent shadow-none px-2.5 py-1 text-xs font-medium h-[26px] leading-tight",
        alert: "text-red-900 bg-transparent border-none hover:bg-red-100",
        // Outline variant per spec
        outline:
          "border border-[#3E6A96] bg-white text-[#3E6A96] hover:bg-gray-50 dark:border-primary-700 dark:text-primary-100 dark:bg-[#202832] dark:hover:bg-[#2a3441]",
        // Danger (contained)button: "text-primary-500 border border-transparent hover:border-primary-500 bg-transparent shadow-none px-3 py-1 text-sm font-medium h-[30px]",
        outline2:
          "border border-primary-600 bg-white text-grey-800 hover:bg-gray-50 dark:border-primary-700 dark:text-primary-100 dark:bg-[#202832] dark:hover:bg-[#2a3441]",
        danger:
          "bg-danger-500 text-white hover:bg-danger-900/90 dark:bg-danger-500 dark:hover:bg-danger-900/80",
        // Fallbacks to keep old imports working
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        noOutline:
          "border-none bg-white text-[#3E6A96] hover:bg-gray-50 dark:text-primary-100 dark:bg-[#202832] dark:hover:bg-[#2a3441]",
      },
      size: {
        // Small: 30px h, 6px 14px padding, radius 4px, font 12/20
        sm: "h-[30px] rounded-[4px] px-[14px] py-[6px] text-[12px] leading-5",
        // Medium: 40px h, 9px 20px padding, radius 6px, font 14/20
        md: "h-[40px] rounded-[6px] px-[20px] py-[9px] text-[14px] leading-5",
        // Large: 50px h, 12px 26px padding, radius 8px, font 16/22
        lg: "h-[50px] rounded-[8px] px-[26px] py-[12px] text-[16px] leading-[22px]",
        // Icon size for square icon-only buttons
        icon: "h-[40px] w-[40px] rounded-[6px]",
        default:
          "h-[40px] rounded-[6px] px-[20px] py-[9px] text-[14px] leading-5",
      },
    },
    defaultVariants: {
      variant: "contained",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  label?: React.ReactNode;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      startIcon,
      endIcon,
      label,
      loading,
      disabled,
      asChild = false,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={loading || disabled}
        {...props}>
        <div className="flex w-full items-center justify-center gap-[10px]">
          {loading ? (
            <Spinner size="sm" />
          ) : (
            startIcon && <span className="inline-flex items-center">{startIcon}</span>
          )}
          
          {label ? label : children}
          
          {endIcon && !loading && (
            <span className="inline-flex items-center">{endIcon}</span>
          )}
        </div>
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
